import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, Loader2, Check, Minus, Wand2, ExternalLink } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { fetchCoverage } from '@/pages/monitoring/services/monitoring.services';
import { generateMonthlySummary } from '@/pages/dashboard/services/monthlySummaries.services';

const MESES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

/**
 * Cobertura de consolidados mensuales: qué cortes existen por centro y mes.
 * Sin corte no hay KPIs persistidos, así que la pestaña de métricas queda
 * ciega para ese centro/mes. Permite generar los faltantes en bloque
 * reutilizando POST /monthly-summaries/generate.
 */
const ConsolidatedCoverageTab = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();

  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(null);
  const [data, setData] = useState(null);

  const load = useCallback(
    async (y) => {
      setLoading(true);
      try {
        const resp = await fetchCoverage(y);
        if (!resp?.state) throw new Error(resp?.message || 'Respuesta inválida');
        setData(resp.data);
      } catch (e) {
        toast({
          title: 'Error',
          description: e.message || 'No se pudo cargar la cobertura de consolidados.',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    },
    [toast],
  );

  useEffect(() => {
    load(year);
  }, [year, load]);

  const rows = data?.laundries || [];

  /** Meses cerrados que tienen al menos un centro sin corte. */
  const mesesConFaltantes = useMemo(() => {
    const meses = new Map();
    rows.forEach((r) => {
      r.meses.forEach((m) => {
        if (m.estado !== 'faltante') return;
        const prev = meses.get(m.month) || [];
        meses.set(m.month, [...prev, r.laundryId]);
      });
    });
    return Array.from(meses.entries())
      .map(([month, laundryIds]) => ({ month, laundryIds }))
      .sort((a, b) => a.month - b.month);
  }, [rows]);

  const totalFaltantes = mesesConFaltantes.reduce((s, m) => s + m.laundryIds.length, 0);

  const generarMes = async ({ month, laundryIds }) => {
    setGenerating(month);
    try {
      const resp = await generateMonthlySummary({
        laundryIds,
        year,
        month,
        generadoPor: user ? { id: user.id, nombre: user.name } : undefined,
      });
      toast({
        title: `Consolidado ${year}-${String(month).padStart(2, '0')}`,
        description: resp?.message || 'Proceso finalizado.',
      });
      await load(year);
    } catch (e) {
      toast({
        title: 'Error',
        description: e.message || 'No se pudo generar el consolidado.',
        variant: 'destructive',
      });
    } finally {
      setGenerating(null);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-4 pb-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs text-muted-foreground">Año</Label>
              <div className="flex gap-2">
                {[currentYear - 1, currentYear].map((y) => (
                  <button
                    key={y}
                    type="button"
                    onClick={() => setYear(y)}
                    className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${
                      year === y
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background text-muted-foreground border-border hover:border-primary/40'
                    }`}
                  >
                    {y}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 ml-auto">
              <Badge variant={totalFaltantes > 0 ? 'warning' : 'success'}>
                {totalFaltantes} corte(s) faltante(s)
              </Badge>
              <Button variant="outline" size="sm" onClick={() => load(year)} disabled={loading}>
                <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Actualizar
              </Button>
              <Button variant="outline" size="sm" onClick={() => navigate('/dashboard/cortes')}>
                Gestión de cortes
                <ExternalLink className="ml-2 h-3.5 w-3.5" />
              </Button>
            </div>
          </div>

          {mesesConFaltantes.length > 0 && (
            <div className="mt-4">
              <Label className="text-xs text-muted-foreground">
                Generar los faltantes de un mes cerrado
              </Label>
              <div className="flex flex-wrap gap-2 mt-1.5">
                {mesesConFaltantes.map((m) => (
                  <Button
                    key={m.month}
                    variant="outline"
                    size="sm"
                    disabled={generating !== null}
                    onClick={() => generarMes(m)}
                  >
                    {generating === m.month ? (
                      <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Wand2 className="mr-2 h-3.5 w-3.5" />
                    )}
                    {MESES[m.month - 1]} · {m.laundryIds.length}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-4">
          {loading && !data ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-3 text-sm text-muted-foreground">Cargando cobertura…</span>
            </div>
          ) : rows.length === 0 ? (
            <p className="text-center text-muted-foreground py-12 text-sm">
              No hay centros de lavado activos.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2.5 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wide w-52 sticky left-0 bg-card">
                      Centro de lavado
                    </th>
                    {MESES.map((m) => (
                      <th
                        key={m}
                        className="text-center py-2.5 px-2 font-medium text-muted-foreground text-xs uppercase tracking-wide min-w-[52px]"
                      >
                        {m}
                      </th>
                    ))}
                    <th className="text-center py-2.5 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wide">
                      Avance
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/50">
                  {rows.map((row, idx) => {
                    const { generados, total } = row.avance;
                    const allDone = total > 0 && generados === total;
                    return (
                      <motion.tr
                        key={row.laundryId}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: Math.min(idx * 0.02, 0.4) }}
                        className="hover:bg-muted/20 transition-colors"
                      >
                        <td className="py-2.5 px-4 font-medium text-foreground sticky left-0 bg-card">
                          {row.nombre}
                        </td>
                        {row.meses.map((m) => (
                          <td key={m.month} className="py-2.5 px-2 text-center">
                            {m.estado === 'generado' ? (
                              <Check
                                className="h-4 w-4 text-emerald-600 dark:text-emerald-400 mx-auto"
                                strokeWidth={2.5}
                              />
                            ) : m.estado === 'faltante' ? (
                              <Minus className="h-4 w-4 text-red-500/70 mx-auto" strokeWidth={2} />
                            ) : (
                              <span
                                className="text-xs text-muted-foreground/30 select-none"
                                title="Mes en curso o futuro: solo se consolidan meses cerrados"
                              >
                                ·
                              </span>
                            )}
                          </td>
                        ))}
                        <td className="py-2.5 px-4 text-center">
                          {total === 0 ? (
                            <span className="text-xs text-muted-foreground/40">—</span>
                          ) : (
                            <span
                              className={`text-sm font-semibold tabular-nums ${
                                allDone
                                  ? 'text-emerald-600 dark:text-emerald-400'
                                  : 'text-muted-foreground'
                              }`}
                            >
                              {generados}/{total}
                            </span>
                          )}
                        </td>
                      </motion.tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ConsolidatedCoverageTab;
